import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Manages "follow tail" behavior for a scrollable container.
 *
 * The user is considered to be following the tail as long as they haven't
 * scrolled more than `threshold` pixels away from the bottom. Scrolling up
 * pauses follow; scrolling back down to the bottom resumes it.
 *
 * Consumers call `jumpToBottom()` after appending new items (or when the
 * user clicks the "новые строки" pill) to force-scroll. The hook also returns
 * `newSinceScroll` — the count of items added while paused, for the pill.
 */
export interface UseFollowTailOptions {
  /** Pixels within the bottom that still count as "at the bottom". */
  threshold?: number;
  /** Current item count. Used to detect new items while paused. */
  itemCount: number;
  /** Whether follow should be active by default. */
  defaultFollowing?: boolean;
}

export interface UseFollowTailResult {
  isFollowing: boolean;
  newSinceScroll: number;
  /** Attach to the scrollable container. */
  registerScrollContainer: (el: HTMLElement | null) => void;
  /** Call this whenever `itemCount` just grew. */
  handleItemsAppended: (scrollToBottom: () => void) => void;
  /** Jump to the bottom and resume following. */
  jumpToBottom: (scrollToBottom: () => void) => void;
  pauseFollow: () => void;
}

export function useFollowTail(opts: UseFollowTailOptions): UseFollowTailResult {
  const { threshold = 32, itemCount, defaultFollowing = true } = opts;

  const [isFollowing, setIsFollowing] = useState(defaultFollowing);
  const [newSinceScroll, setNewSinceScroll] = useState(0);
  const scrollElRef = useRef<HTMLElement | null>(null);
  const lastCountRef = useRef(itemCount);
  const isFollowingRef = useRef(isFollowing);

  useEffect(() => {
    isFollowingRef.current = isFollowing;
    if (isFollowing) setNewSinceScroll(0);
  }, [isFollowing]);

  const registerScrollContainer = useCallback(
    (el: HTMLElement | null) => {
      scrollElRef.current = el;
      if (!el) return;

      const onScroll = () => {
        const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
        const atBottom = distanceFromBottom <= threshold;
        if (atBottom && !isFollowingRef.current) {
          setIsFollowing(true);
        } else if (!atBottom && isFollowingRef.current) {
          setIsFollowing(false);
        }
      };

      el.addEventListener("scroll", onScroll, { passive: true });
      return () => el.removeEventListener("scroll", onScroll);
    },
    [threshold]
  );

  const handleItemsAppended = useCallback(
    (scrollToBottom: () => void) => {
      const delta = itemCount - lastCountRef.current;
      lastCountRef.current = itemCount;
      if (delta <= 0) return;

      if (isFollowingRef.current) {
        // Defer to the next frame so the virtualizer has measured new rows.
        requestAnimationFrame(() => scrollToBottom());
      } else {
        setNewSinceScroll((n) => n + delta);
      }
    },
    [itemCount]
  );

  const jumpToBottom = useCallback((scrollToBottom: () => void) => {
    setIsFollowing(true);
    setNewSinceScroll(0);
    requestAnimationFrame(() => scrollToBottom());
  }, []);

  const pauseFollow = useCallback(() => {
    setIsFollowing(false);
  }, []);

  return {
    isFollowing,
    newSinceScroll,
    registerScrollContainer,
    handleItemsAppended,
    jumpToBottom,
    pauseFollow,
  };
}
